import React, { useContext, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import { GridRowParams, GridValueFormatterParams } from '@mui/x-data-grid';

import CustomDataGrid from '../../components/data-grid';
import CustomDataRowSelectionGrid from '../../components/data-grid-selection';
import Screen from '../../components/screen';
import SplashWait from '../../components/splash-wait';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import API from '../../utils/api';
import {
  apiReponseError, apiRequestError, canReadAccountOptout, canWriteAccountOptout,
  checkUserAuthenticated, formatDateForDataGrid, getAccountOptouts, goToRoutePage, setWindowTitle
} from '../../utils/helper';
import { AddFileIcon, MoreIcon, RefreshIcon } from '../../utils/icons';
import {
  Box, Breadcrumbs, Button, FormControl, Grid, Hidden, IconButton, InputLabel, Link, ListItemIcon,
  ListItemText, Menu, MenuItem, Select, Tooltip, Typography
} from '../../utils/material-ui-core';
import UploadOptoutFile from './uploadOptoutFile';

const StyledMenuItem = withStyles((theme) => ({
  root: {
    "&:focus": {
      backgroundColor: theme.palette.secondary.main,
      "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem);

const AccountOptout = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { isUserAuthenticated, showAlert, userFunctionAccess, referenceLookups } = useContext(AppContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [readonly, setReadonly] = useState<boolean>(true);
  const [openFileUploadDialog, setOpenFileUploadDialog] = useState<boolean>(false);
  const [gridMoreAnchorEl, setGridMoreAnchorEl] = React.useState(null);
  const [accountOptoutItems, setAccountOptoutItems] = useState<AccountOptoutItem[]>([]);
  const [filteredAccountOptoutItems, setFilteredAccountOptoutItems] = useState<AccountOptoutItem[]>([]);
  const [companies, setCompanies] = useState<AccountOptoutCompany[]>([]);
  const [clientCompanyCode, setClientCompanyCode] = useState<string>("");
  const [clientCompanyKey, setClientCompanyKey] = useState<string>("");
  const [displayMode, setDisplayMode] = useState<number>(0);
  const [action, setAction] = useState<number>(0);
  const [statusUpdateFileKeys, setStatusUpdateFileKeys] = useState<any[]>([]);
  const [statusUpdateFileItemKeys, setStatusUpdateFileItemKeys] = useState<any[]>([]);

  const columnsForAccount = [
    { field: "fileId", headerName: "File Id", width: 130, type: "number" },
    { field: "companyCode", headerName: "Company Code", width: 200, hide: true },
    { field: "companyName", headerName: "Company Name", width: 300, hide: true },
    { field: "validCompany", headerName: "Valid Company", width: 200, type: "boolean" },
    { field: "programCode", headerName: "Program Code", width: 200 },
    { field: "programName", headerName: "Program Name", width: 300, hide: true },
    { field: "validProgram", headerName: "Valid Program", width: 170, type: "boolean" },
    { field: "accountNumber", headerName: "Account #", width: 200, },
    { field: "isValidationCompleted", headerName: "Validation Completed", width: 300, type: "boolean" },
    { field: "validationMessage", headerName: "Validation Message", width: 600 },
    { field: "effectiveDate", headerName: "EffectiveDate", width: 300, type: "dateTime", valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "userDefinedField1", headerName: "user Defined Field 1", width: 300 },
    { field: "userDefinedField2", headerName: "user Defined Field 2", width: 300 },
    { field: "userDefinedField3", headerName: "user Defined Field 3", width: 300 },
    { field: "userDefinedField4", headerName: "user Defined Field 4", width: 300 },
    { field: "userDefinedField5", headerName: "user Defined Field 5", width: 300 },
    { field: "lastModifiedBy", headerName: "Last Modified By", width: 250, hide: true, },
    { field: "lastModifiedDateTime", headerName: "Last Modified Date", width: 250, type: "dateTime", hide: false, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "statusUpdateFileKey", headerName: "Key 1", width: 350, hide: true },
    { field: "statusUpdateFileItemKey", headerName: "Key 2", width: 350, hide: true },
  ];

  const columnsForFile = [
    { field: "fileId", headerName: "File Id", width: 130, type: "number" },
    { field: "companyCode", headerName: "Company Code", width: 200, hide: true },
    { field: "companyName", headerName: "Company Name", width: 300, hide: true },
    { field: "validCompany", headerName: "Valid Company", width: 200, type: "boolean" },
    { field: "isValidationCompleted", headerName: "Validation Completed", width: 300, type: "boolean" },
    { field: "validationMessage", headerName: "Validation Message", width: 600 },
    { field: "lastModifiedBy", headerName: "Last Modified By", width: 250, hide: true, },
    { field: "lastModifiedDateTime", headerName: "Last Modified Date", width: 250, type: "dateTime", hide: false, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "statusUpdateFileKey", headerName: "Key 1", width: 350, hide: true },
    { field: "statusUpdateFileItemKey", headerName: "Key 2", width: 350, hide: true },
  ];

  const save = async () => {

    if (statusUpdateFileKeys.length + statusUpdateFileItemKeys.length <= 0) {
      showAlert({ message: "Select one or more rows to process.", severity: "error", });
      return;
    }

    setLoading(true);

    try {

      const payload = {
        fileKeys: statusUpdateFileKeys,
        fileItemKeys: statusUpdateFileItemKeys
      }

      await API.post("/api/accountoptouts/close", payload)
        .then(async (response) => {

          showAlert({
            message: "Successfully closed all selected row(s).",
            severity: "success",
          });

          setStatusUpdateFileKeys([]);
          setStatusUpdateFileItemKeys([]);

          await refresh();

          setLoading(false);

        })
        .catch((error) => {
          setLoading(false);
          showAlert({
            message: apiReponseError(error, "Failed to save."),
            severity: "error",
          });
        });
    } catch (e) {
      setLoading(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  }

  const handleSelectedRowsForPendingAssignments = (rows: any[]) => {

    const tempStatusUpdateFileKeys: any[] = [];
    const tempStatusUpdateFileItemKeys: any[] = [];
    let statusUpdateFileKey = null;
    let statusUpdateFileItemKey = null;

    setStatusUpdateFileKeys([]);
    setStatusUpdateFileItemKeys([]);

    rows.forEach(row => {
      statusUpdateFileKey = null;
      statusUpdateFileItemKey = null;
      if (displayMode === 1) {
        statusUpdateFileKey = row.statusUpdateFileKey;
      }
      else if (displayMode === 2) {
        statusUpdateFileItemKey = row.statusUpdateFileItemKey;
      }
      else {
        statusUpdateFileKey = row.statusUpdateFileItemKey === null ? row.statusUpdateFileKey : null;
        statusUpdateFileItemKey = row.statusUpdateFileItemKey !== null ? row.statusUpdateFileItemKey : null;
      }
      if (statusUpdateFileKey !== null && tempStatusUpdateFileKeys.indexOf(statusUpdateFileKey) === -1) {
        tempStatusUpdateFileKeys.push(statusUpdateFileKey);
      }
      if (statusUpdateFileItemKey !== null && tempStatusUpdateFileItemKeys.indexOf(statusUpdateFileItemKey) === -1) {
        tempStatusUpdateFileItemKeys.push(statusUpdateFileItemKey);
      }
    });

    setStatusUpdateFileKeys(tempStatusUpdateFileKeys);
    setStatusUpdateFileItemKeys(tempStatusUpdateFileItemKeys);

  }

  // More menu click event
  const handleGridMoreMenuClick = (event) => {
    setGridMoreAnchorEl(event.currentTarget);
  };

  // Close more menu
  const handleGridMoreMenuClose = () => {
    setGridMoreAnchorEl(null);
  };

  const refresh = async () => {
    await search();
  }

  // Open upload file dialog
  const displayFileUploadDialog = () => {
    setOpenFileUploadDialog(true);
  };

  // Confirmation from upload file dialog
  const confirmFileUploadDialog = async (confirmOutput: ConfirmOutput) => {
    setOpenFileUploadDialog(false);
  };

  const handleDataGridClick = (params: GridRowParams, event: any) => { };

  const getFilteredByCompanyCode = (data, code) => {
    setFilteredAccountOptoutItems([]);

    const rows: any = [];
    data.filter(accountOptoutItem => accountOptoutItem.companyCode === code).map((accountOptoutItem, index) =>
      rows.push({
        id: index,
        ...accountOptoutItem
      })
    );

    const filterClientCompanies = referenceLookups.clientCompanies.filter(x => x.hsbPartnerId === code);

    if (filterClientCompanies.length === 1) {
      setClientCompanyKey(filterClientCompanies[0].clientCompanyKey)
    }
    else {
      setClientCompanyKey("");
    }
    setFilteredAccountOptoutItems(rows);
  };

  const search = async () => {
    setLoading(true);

    setAccountOptoutItems([]);
    setCompanies([]);

    setLoading(true);

    const apiResponse = await getAccountOptouts();

    if (apiResponse.success) {
      const data = apiResponse.data;
      setAccountOptoutItems(data.accountOptoutItems as AccountOptoutItem[]);

      const uniqueCompanies = data.companies as AccountOptoutCompany[];

      setCompanies(uniqueCompanies);

      let tempClientCompanyCode = clientCompanyCode;

      if (uniqueCompanies.length > 0) {
        if ((data.accountOptoutItems as AccountOptoutItem[]).filter(item => item.companyCode === tempClientCompanyCode).length <= 0) {
          tempClientCompanyCode = uniqueCompanies[0].companyCode;
          setClientCompanyCode(tempClientCompanyCode);
        }
      }


      getFilteredByCompanyCode(data.accountOptoutItems, tempClientCompanyCode);

    } else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      return;
    }

    setLoading(false);
  };

  useEffect(() => {
    setWindowTitle("Account Optouts");
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const initializePage = async () => {
      checkUserAuthenticated(isUserAuthenticated);

      if (!canReadAccountOptout(userFunctionAccess)) {
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("home");
        return;
      }

      if (isUserAuthenticated) {
        if (canWriteAccountOptout(userFunctionAccess)) {
          setReadonly(false);
        }

        await search();
        setLoading(false);
      }
    };

    initializePage();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated]);

  return (
    <React.Fragment>
      {
        loading ? (
          <SplashWait />
        ) : (
          <Screen>
            <UploadOptoutFile open={openFileUploadDialog} clientCompanyKey={clientCompanyKey} onCallback={confirmFileUploadDialog} />

            <Box className={pageStyles.root}>
              <Grid container spacing={3}>
                <Grid item xs={9}>
                  <Box className={globalStyles.bottomSpacer}>
                    <Breadcrumbs aria-label="breadcrumb">
                      <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                        Home
                      </Link>
                      <Typography color="textPrimary">Account Optout</Typography>
                    </Breadcrumbs>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Hidden xsDown>
                    <Box display="flex" justifyContent="flex-end" width={1}>
                      {canWriteAccountOptout(userFunctionAccess) && !readonly && clientCompanyKey !== "" ? (
                        <IconButton>
                          <Tooltip arrow title={"Upload Optout File"}>
                            <AddFileIcon color="secondary" onClick={displayFileUploadDialog} />
                          </Tooltip>
                        </IconButton>
                      ) : null}

                      <IconButton>
                        <Tooltip arrow title={"Refresh"}>
                          <RefreshIcon color="secondary" onClick={() => refresh()} />
                        </Tooltip>
                      </IconButton>
                    </Box>
                  </Hidden>


                  <Hidden smUp>
                    <Box display="flex" justifyContent="flex-end" width={1}>
                      <Tooltip arrow title="Menu">
                        <Button color="secondary" aria-controls="fade-menu" aria-haspopup="true" onClick={handleGridMoreMenuClick}>
                          <MoreIcon />
                        </Button>
                      </Tooltip>
                      <Menu elevation={2} id="grid-menu" anchorEl={gridMoreAnchorEl} keepMounted open={Boolean(gridMoreAnchorEl)} onClose={handleGridMoreMenuClose}>


                        {canWriteAccountOptout(userFunctionAccess) && clientCompanyKey !== "" ? (
                          <>
                            <StyledMenuItem
                              onClick={(e) => {
                                displayFileUploadDialog();
                                handleGridMoreMenuClose();
                              }}
                            >
                              <ListItemIcon>
                                <AddFileIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={"Upload Optout File"} />
                            </StyledMenuItem>

                          </>
                        ) : null}

                        <StyledMenuItem
                          onClick={(e) => {
                            refresh();
                            handleGridMoreMenuClose();
                          }}
                        >
                          <ListItemIcon>
                            <RefreshIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={"Refresh "} />
                        </StyledMenuItem>
                      </Menu>
                    </Box>
                  </Hidden>


                </Grid>
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={8} md={6} lg={4} xl={4}>
                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                    <InputLabel shrink={true}>Company</InputLabel>
                    <Select
                      required
                      native
                      labelId="company-code-label"
                      id="clientCompanyCode"
                      name="clientCompanyCode"
                      value={clientCompanyCode}
                      onChange={(e) => {
                        if (e !== null) {
                          setClientCompanyCode(e.target.value as string);
                          getFilteredByCompanyCode(accountOptoutItems, e.target.value as string);
                        }

                      }}
                      label="Company"
                    >
                      <optgroup label="Existing Entities">
                        {
                          companies.filter(company => company.dataExists === true).map((company, index) => (
                            <option value={company.companyCode} key={index}>
                              {company.name} ({company.companyCode})
                            </option>
                          ))
                        }
                      </optgroup>
                      <optgroup label="New Entities">
                        {
                          companies.filter(company => company.dataExists === false).map((company, index) => (
                            <option value={company.companyCode} key={index}>
                              {company.name} ({company.companyCode})
                            </option>
                          ))
                        }
                      </optgroup>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={4} lg={2} xl={2}>
                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                    <InputLabel shrink={true}>Show</InputLabel>
                    <Select
                      required
                      native
                      labelId="display-mode-label"
                      id="displayMode"
                      name="displayMode"
                      value={displayMode}
                      onChange={(e) => {
                        if (e !== null) {
                          setDisplayMode(e.target.value as number);
                        }

                      }}
                      label="Display Mode"
                    >
                      <option value={0}>All</option>
                      <option value={1}>Only File Issues ({filteredAccountOptoutItems.filter(data => data.statusUpdateFileItemKey === null).length})</option>
                      <option value={2}>Only Account Issues ({filteredAccountOptoutItems.filter(data => data.statusUpdateFileItemKey !== null).length})</option>
                    </Select>
                  </FormControl>
                </Grid>

                {
                  Number(displayMode) === 1 ?
                    <Grid item xs={12} >
                      <Box className={pageStyles.datagridContainer}   >
                        {readonly ? 1 : 0}
                        {
                          !readonly ?
                            <CustomDataRowSelectionGrid
                              uniqueGridCode={"AccountOptoutsResult-File-2"}
                              columns={columnsForFile}
                              rows={filteredAccountOptoutItems.filter(data => data.statusUpdateFileItemKey === null)}
                              showToolBar={true}
                              sortModel={[{ field: "fileId", sort: "desc" }]}
                              handleDataGridClick={() => { }}
                              handleSelectedRows={handleSelectedRowsForPendingAssignments} />
                            :
                            <CustomDataGrid
                              uniqueGridCode={"AccountOptoutsResult-File-1"}
                              columns={columnsForFile}
                              rows={filteredAccountOptoutItems.filter(data => data.statusUpdateFileItemKey === null)}
                              showToolBar={true}
                              sortModel={[{ field: "fileId", sort: "desc" }]}
                              handleDataGridClick={handleDataGridClick} />
                        }

                      </Box>
                    </Grid>
                    :
                    Number(displayMode) === 2 ?
                      <Grid item xs={12}  >
                        <Box className={pageStyles.datagridContainer}  >
                          {
                            !readonly ?
                              <CustomDataRowSelectionGrid
                                uniqueGridCode={"AccountOptoutsResult-Account -1"}
                                columns={columnsForAccount}
                                rows={filteredAccountOptoutItems.filter(data => data.statusUpdateFileItemKey !== null)}
                                showToolBar={true}
                                sortModel={[{ field: "fileId", sort: "desc" }]}
                                handleDataGridClick={() => { }}
                                handleSelectedRows={handleSelectedRowsForPendingAssignments} />
                              :
                              <CustomDataGrid
                                uniqueGridCode={"AccountOptoutsResult-Account-2"}
                                columns={columnsForAccount}
                                rows={filteredAccountOptoutItems.filter(data => data.statusUpdateFileItemKey !== null)}
                                showToolBar={true}
                                sortModel={[{ field: "fileId", sort: "desc" }]}
                                handleDataGridClick={handleDataGridClick} />
                          }

                        </Box>
                      </Grid>
                      :
                      <Grid item xs={12}  >
                        <Box className={pageStyles.datagridContainer}  >
                          {
                            !readonly ?
                              <CustomDataRowSelectionGrid
                                uniqueGridCode={"AccountOptoutsResult-All-1"}
                                columns={columnsForAccount}
                                rows={filteredAccountOptoutItems}
                                showToolBar={true}
                                sortModel={[{ field: "fileId", sort: "desc" }]}
                                handleDataGridClick={() => { }}
                                handleSelectedRows={handleSelectedRowsForPendingAssignments} />
                              :
                              <CustomDataGrid
                                uniqueGridCode={"AccountOptoutsResult-All-2"}
                                columns={columnsForAccount}
                                rows={filteredAccountOptoutItems}
                                showToolBar={true}
                                sortModel={[{ field: "fileId", sort: "desc" }]}
                                handleDataGridClick={handleDataGridClick} />
                          }


                        </Box>
                      </Grid>
                }


                <Grid item xs={12}>
                  {
                    statusUpdateFileKeys.length > 0 || statusUpdateFileItemKeys.length > 0 ?
                      <Grid container>
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                          <FormControl className={globalStyles.formControl} fullWidth={true}>
                            <InputLabel shrink={true}>Action</InputLabel>
                            <Select
                              required
                              native
                              labelId="action-label"
                              id="action"
                              name="action"
                              value={action}
                              onChange={(e) => {
                                if (e !== null) {
                                  setAction(e.target.value as number);
                                }
                              }}
                              label="Action"
                            >
                              <option value={0}></option>
                              <option value={1}>Close</option>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <Box p={3} display="flex" justifyContent="center" alignContent="center">
                            {
                              action > 0 ?
                                <Button
                                  className={globalStyles.buttonDefault}
                                  color="secondary"
                                  variant='contained'
                                  onClick={() => { save() }}>
                                  Update
                                </Button>
                                : null
                            }
                          </Box>
                        </Grid>
                      </Grid>
                      : null
                  }

                </Grid>
              </Grid>
            </Box>
          </Screen>)
      }
    </React.Fragment>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    datagridContainer: {
      minHeight: "350px",
      height: "calc(100vh - 400px)",
      width: "100%",
      marginBottom: theme.spacing(2),
    },
  })
);

export default AccountOptout;
